import { Sparkles, AlertTriangle, ShieldAlert, Info, TrendingUp } from 'lucide-react';
import buildQualityNarrative from '@/lib/qualityNarrative';
import AbbrText from '@/components/AbbrText';

/**
 * HeadlineInsight — the single most actionable narrative bullet for a
 * stock, rendered as a slim banner-style ribbon at the top of the
 * Stock Modal between the header and the tabs.
 *
 * Priority order (highest signal first):
 *   1. Trap-flag negative bullets from the Triangle narrative
 *   2. Trap-flag negative bullets from the Quality narrative
 *      (typically "payout ratio > 100%" type warnings)
 *   3. Caution bullets (stretched payout, EPS lagging, elevated debt)
 *   4. Positive bullets (long streak, strong ROIC, comfortable payout)
 *   5. Info bullets (ETF caveat, TTM window)
 *
 * Source: Quality narrative pulls from `stock` directly (no fetch);
 * Triangle narrative is only available if `stock` already carries the
 * `triangle_score`/`triangle_legs` fields that were mirrored by the
 * backend after a Triangle view. That means newer tickers without a
 * cached Triangle still get a Quality-derived headline.
 */
const TONE_BAR = {
  negative: 'bg-rose-50/80 dark:bg-rose-950/40 border-rose-200/60 dark:border-rose-800/50 text-rose-900 dark:text-rose-100',
  caution: 'bg-amber-50/80 dark:bg-amber-950/40 border-amber-200/60 dark:border-amber-800/50 text-amber-900 dark:text-amber-100',
  positive: 'bg-emerald-50/80 dark:bg-emerald-950/40 border-emerald-200/60 dark:border-emerald-800/50 text-emerald-900 dark:text-emerald-100',
  info: 'bg-sky-50/80 dark:bg-sky-950/40 border-sky-200/60 dark:border-sky-800/50 text-sky-900 dark:text-sky-100',
};

const TONE_ICON = {
  negative: <ShieldAlert className="w-3.5 h-3.5 shrink-0" />,
  caution: <AlertTriangle className="w-3.5 h-3.5 shrink-0" />,
  positive: <TrendingUp className="w-3.5 h-3.5 shrink-0" />,
  info: <Info className="w-3.5 h-3.5 shrink-0" />,
};

const TONE_PRIORITY = { negative: 0, caution: 1, positive: 2, info: 3 };

function _pickHeadline(stock) {
  // Quality narrative is always available from cached fields. The
  // first bullet is the composed "Quality Score is X (band) because:"
  // summary — we want a *targeted* bullet (rule 2+), not the summary,
  // because the summary is less surprising. If only the summary
  // exists, fall back to it.
  const quality = buildQualityNarrative(stock) || [];

  // Triangle narrative isn't shipped to the frontend on every row —
  // it lives in the /api/stocks/{ticker}/triangle response. But the
  // trap_flag IS mirrored to stock_cache. If the row knows it's a
  // trap, synthesize a lead bullet so the ribbon fires immediately.
  const synthetic = [];
  if (stock?.triangle_trap_flag) {
    synthetic.push({
      tone: 'negative',
      text: 'Triangle detector flagged this as a potential Dividend Trap — dividend growth is outpacing the underlying business. Open the Triangle tab for the full breakdown.',
    });
  }

  const all = [...synthetic, ...quality];

  // Prefer a targeted bullet (rule 2+ from Quality) over the summary;
  // the summary always starts with "Quality Score is" so we can
  // detect it. Within the same tone class, targeted beats summary.
  const targeted = all.filter(b =>
    b && b.text && !b.text.startsWith('Quality Score is ')
  );
  const pool = targeted.length > 0 ? targeted : all;

  if (pool.length === 0) return null;

  // Sort by tone priority; on tie, keep array order (rules already
  // run in priority).
  pool.sort((a, b) =>
    (TONE_PRIORITY[a.tone] ?? 99) - (TONE_PRIORITY[b.tone] ?? 99)
  );
  return pool[0];
}

export default function HeadlineInsight({ stock, onSeeMore, onJumpToCashLens, section19aUrl }) {
  if (!stock) return null;
  const item = _pickHeadline(stock);
  if (!item) return null;
  const tone = item.tone || 'info';

  // Round 92o+ — when the bullet itself redirects the reader to the
  // REIT/MLP Cash Lens (e.g., "see FFO Cash Lens below"), replace the
  // generic "More" CTA with a targeted "Cash Lens →" button that
  // switches the modal to the Stock Metrics tab AND scrolls down to
  // the panel. Closes the navigation loop the bullet is asking for.
  const cashLensMatch = /(?:FFO|DCF|REIT|MLP)\s+Cash\s+Lens/i.test(item.text || '');

  // Round 86 (Feb 2026) — Linkify "Section 19(a) notices" within the
  // bullet text when the issuer's distributions URL is known. Opens in
  // a new tab — same target as the link on the Dividend Info tab.
  const renderText = () => {
    const text = item.text || '';
    const marker = 'Section 19(a) notices';
    const idx = text.indexOf(marker);
    if (!section19aUrl || idx === -1) {
      return <AbbrText text={text} as="span" />;
    }
    const before = text.slice(0, idx);
    const after = text.slice(idx + marker.length);
    return (
      <>
        <AbbrText text={before} as="span" />
        <a
          href={section19aUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary hover:underline"
          data-testid="stock-modal-headline-19a-link"
          title="Opens the issuer's distributions page in a new tab."
        >
          {marker}
        </a>
        <AbbrText text={after} as="span" />
      </>
    );
  };

  return (
    <div
      className={`flex items-start gap-2 px-3 sm:px-4 py-1.5 border-b text-[11px] sm:text-xs font-medium ${TONE_BAR[tone] || TONE_BAR.info}`}
      data-testid="stock-modal-headline-insight"
    >
      <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5 text-violet-500" />
      <span className="flex-1 leading-snug">
        {renderText()}
      </span>
      {cashLensMatch && onJumpToCashLens && (
        <button
          type="button"
          onClick={onJumpToCashLens}
          className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border border-current/40 hover:bg-current/10 transition-colors"
          data-testid="stock-modal-headline-cash-lens"
          title="Jump to the REIT/MLP Cash Lens panel"
        >
          Cash Lens →
        </button>
      )}
      {!cashLensMatch && onSeeMore && (
        <button
          type="button"
          onClick={onSeeMore}
          className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border border-current/30 hover:bg-current/10 transition-colors"
          data-testid="stock-modal-headline-see-more"
          title="Jump to the full Quality Score breakdown"
        >
          More
        </button>
      )}
      <span className="shrink-0 mt-0.5" aria-hidden>
        {TONE_ICON[tone] || TONE_ICON.info}
      </span>
    </div>
  );
}
